import {
  Button,
  FormControl,
  Grid2 as Grid,
  OutlinedInput,
  Select,
  MenuItem,
  ListItemText,
  Box,
  Chip,
  Typography,
  Paper,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  IconButton,
  Popover,
} from "@mui/material";
import {
  Search as SearchIcon,
  Tune as TuneIcon,
  RestartAlt as ResetIcon,
} from "@mui/icons-material";
import { useState, useEffect } from "react";

/**
 * QNA 컨텐츠 필터 오가니즘 Props
 */
export type FilterProps = {
  /**
   * 현재 필터 값들
   */
  values: {
    category: string;
    keyword: string;
    useYn: string | null | undefined;
  };
  /**
   * 필터 변경 이벤트
   */
  onChange: (values: {
    category: string;
    keyword: string;
    useYn: string;
  }) => void;
  /**
   * 필터 초기화 이벤트
   */
  onReset: () => void;
};

/**
 * QNA 컨텐츠 필터 오가니즘
 *
 * @description 도장 필터를 참고하여 구현
 * @param props FilterProps
 * @returns JSX.Element
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const Filter = (props: FilterProps) => {
  const { values, onChange, onReset } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [formValues, setFormValues] = useState({
    category: values.category || "",
    keyword: values.keyword || "",
    useYn:
      values.useYn === null || values.useYn === undefined ? "" : values.useYn,
  });

  // props가 변경될 때 formValues 업데이트
  useEffect(() => {
    setFormValues({
      category: values.category || "",
      keyword: values.keyword || "",
      useYn:
        values.useYn === null || values.useYn === undefined ? "" : values.useYn,
    });
  }, [values]);

  const handleOpenFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setAnchorEl(null);
  };

  const handleApplyFilters = () => {
    onChange(formValues);
    handleCloseFilters();
  };

  const handleSearch = () => {
    onChange(formValues);
  };

  const handleReset = () => {
    const defaultValues = {
      category: "",
      keyword: "",
      useYn: "",
    };
    setFormValues(defaultValues);
    onReset();
    handleCloseFilters();
  };

  const handleKeywordChange = (value: string) => {
    setFormValues({ ...formValues, keyword: value });
  };

  const handleCategoryChange = (value: string) => {
    setFormValues({ ...formValues, category: value });
  };

  const handleUseYnChange = (value: string | undefined | null) => {
    const cleanValue = value === null || value === undefined ? "" : value;
    setFormValues({ ...formValues, useYn: cleanValue });
  };

  const open = Boolean(anchorEl);
  const id = open ? "filters-popover" : undefined;

  // 카테고리 옵션
  const categoryOptions = [
    { value: "GENERAL", label: "일반 문의" },
    { value: "REGISTRATION", label: "등록 관련" },
    { value: "SCHEDULE", label: "수업 일정" },
    { value: "PAYMENT", label: "결제 관련" },
    { value: "FACILITY", label: "시설 안내" },
    { value: "PROGRAM", label: "프로그램 안내" },
    { value: "EVENT", label: "이벤트" },
    { value: "CONTACT", label: "연락처" },
    { value: "OTHER", label: "기타" },
  ];

  // 사용 상태 표시 문자열 생성
  const getUseYnDisplayName = (useYn: string | undefined | null) => {
    if (useYn === null || useYn === undefined) {
      return null;
    }

    switch (useYn) {
      case "Y":
        return "사용";
      case "N":
        return "미사용";
      case "":
        return "전체";
      default:
        return null;
    }
  };

  // 활성화된 필터 태그 생성
  const renderFilterTags = () => {
    const tags = [];

    if (formValues.keyword) {
      tags.push(
        <Chip
          key="keyword"
          label={`검색어: ${formValues.keyword}`}
          size="small"
          onDelete={() => handleKeywordChange("")}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    if (formValues.category) {
      const category = categoryOptions.find(
        (c) => c.value === formValues.category
      );
      if (category) {
        tags.push(
          <Chip
            key="category"
            label={`카테고리: ${category.label}`}
            size="small"
            onDelete={() => handleCategoryChange("")}
            sx={{ mr: 1, mb: 1 }}
          />
        );
      }
    }

    if (
      formValues.useYn &&
      formValues.useYn !== "" &&
      formValues.useYn !== null &&
      formValues.useYn !== undefined
    ) {
      const displayName = getUseYnDisplayName(formValues.useYn);
      if (displayName && displayName !== "전체") {
        tags.push(
          <Chip
            key="useYn"
            label={`상태: ${displayName}`}
            size="small"
            onDelete={() => handleUseYnChange("")}
            sx={{ mr: 1, mb: 1 }}
          />
        );
      }
    }

    return tags.length > 0 ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2 }}>
        {tags}
        {tags.length > 0 && (
          <Chip
            label="모두 지우기"
            size="small"
            variant="outlined"
            onClick={handleReset}
            deleteIcon={<ResetIcon />}
            sx={{ mb: 1 }}
          />
        )}
      </Box>
    ) : null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        mb: 3,
      }}
    >
      {/* 메인 검색바 */}
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 8, md: 10 }}>
          <FormControl fullWidth variant="outlined" size="small">
            <OutlinedInput
              placeholder="컨텐츠 내용, 키워드로 검색..."
              value={formValues.keyword || ""}
              onChange={(event) => {
                handleKeywordChange(event.target.value);
              }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon color={searchFocused ? "primary" : "action"} />
                </InputAdornment>
              }
              sx={{
                transition: "all 0.2s",
                boxShadow: searchFocused ? 1 : 0,
                "&.Mui-focused": {
                  borderColor: "primary.main",
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 4, md: 2 }} sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="고급 필터 옵션">
            <IconButton
              color="primary"
              onClick={handleOpenFilters}
              aria-describedby={id}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                flex: 1,
              }}
            >
              <TuneIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            disableElevation
            onClick={handleSearch}
            sx={{ flex: 3 }}
          >
            검색
          </Button>
        </Grid>
      </Grid>

      {/* 선택된 필터 태그 */}
      {renderFilterTags()}

      {/* 고급 필터 팝오버 */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseFilters}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: 320,
            p: 2,
            mt: 1,
          },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          고급 필터
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            카테고리
          </Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              value={formValues.category || ""}
              onChange={(event) => {
                handleCategoryChange(event.target.value);
              }}
              displayEmpty
              sx={{ mt: 0.5 }}
            >
              <MenuItem value="">
                <ListItemText primary="전체" />
              </MenuItem>
              {categoryOptions.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  <ListItemText primary={category.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            사용상태
          </Typography>
          <ToggleButtonGroup
            value={formValues.useYn || null}
            exclusive
            onChange={(_, value) => handleUseYnChange(value || "")}
            fullWidth
            size="small"
            sx={{ mt: 0.5 }}
          >
            <ToggleButton value="" aria-label="전체">
              전체
            </ToggleButton>
            <ToggleButton value="Y" aria-label="사용">
              사용
            </ToggleButton>
            <ToggleButton value="N" aria-label="미사용">
              미사용
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleReset}
            startIcon={<ResetIcon />}
          >
            초기화
          </Button>
          <Button
            variant="contained"
            disableElevation
            size="small"
            onClick={handleApplyFilters}
          >
            적용
          </Button>
        </Box>
      </Popover>
    </Paper>
  );
};
